// Generate a branded PNG of the user's mastery trophy and either share it via
// the Web Share API (native share sheet on mobile) or fall back to a download.
// Zero dependencies — uses only canvas + navigator.share.

import QRCode from "qrcode";

const BRAND = "Octave";

function ordinal(n) {
  const v = n % 100;
  if (v >= 11 && v <= 13) return "th";
  switch (n % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
}

function drawTrophy(ctx, cx, cy, scale = 1) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(scale, scale);
  ctx.fillStyle = "#3d2a00";
  ctx.strokeStyle = "#3d2a00";
  ctx.lineWidth = 14;
  ctx.lineCap = "round";
  // cup bowl
  ctx.beginPath();
  ctx.moveTo(-90, -110);
  ctx.lineTo(90, -110);
  ctx.lineTo(70, 20);
  ctx.quadraticCurveTo(0, 55, -70, 20);
  ctx.closePath();
  ctx.fill();
  // handles
  ctx.beginPath(); ctx.arc(-110, -55, 32, -Math.PI * 0.4, Math.PI * 0.9); ctx.stroke();
  ctx.beginPath(); ctx.arc( 110, -55, 32, Math.PI * 0.1, Math.PI * 1.4); ctx.stroke();
  // stem
  ctx.fillRect(-20, 20, 40, 50);
  // base
  ctx.fillStyle = "#3d2a00";
  ctx.beginPath();
  ctx.roundRect ? ctx.roundRect(-85, 70, 170, 28, 6) : ctx.rect(-85, 70, 170, 28);
  ctx.fill();
  // shine
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.beginPath();
  ctx.ellipse(-30, -70, 20, 10, -0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawRays(ctx, cx, cy, count = 32, color = "#FFF3B0", alpha = 0.22) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  for (let i = 0; i < count; i++) {
    ctx.save();
    ctx.rotate((i * (360 / count) * Math.PI) / 180);
    ctx.fillRect(-4, -700, 8, 1400);
    ctx.restore();
  }
  ctx.restore();
}

export async function generateTrophyImage({ userName, total, itemTitle, milestone }) {
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background gradient
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#FFE28A");
  bg.addColorStop(0.55, "#FFB703");
  bg.addColorStop(1, "#8B5A00");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Rays behind trophy
  drawRays(ctx, W / 2, 560);

  // Glowing disc
  const r = 230;
  const disc = ctx.createRadialGradient(W / 2 - 60, 500, 60, W / 2, 560, r);
  disc.addColorStop(0, "#FFF6CC");
  disc.addColorStop(0.55, "#FFB703");
  disc.addColorStop(1, "#B77800");
  ctx.fillStyle = disc;
  ctx.beginPath();
  ctx.arc(W / 2, 560, r, 0, Math.PI * 2);
  ctx.fill();

  // Outer ring
  ctx.lineWidth = 10;
  ctx.strokeStyle = "rgba(255,255,255,0.6)";
  ctx.beginPath();
  ctx.arc(W / 2, 560, r - 14, 0, Math.PI * 2);
  ctx.stroke();

  // Trophy
  drawTrophy(ctx, W / 2, 560, 1.15);

  // Milestone pill (only shown on milestones)
  if (milestone) {
    const pillText = `★  ${milestone} MASTERED — MILESTONE UNLOCKED  ★`;
    ctx.font = "700 34px 'Outfit', 'Helvetica Neue', Arial, sans-serif";
    const tw = ctx.measureText(pillText).width;
    const px = (W - tw - 60) / 2;
    const py = 130;
    ctx.fillStyle = "#3d2a00";
    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(px, py, tw + 60, 66, 33) : ctx.rect(px, py, tw + 60, 66);
    ctx.fill();
    ctx.fillStyle = "#FFE28A";
    ctx.textBaseline = "middle";
    ctx.fillText(pillText, W / 2 - tw / 2, py + 33);
    ctx.textBaseline = "alphabetic";
  }

  // Headline
  ctx.textAlign = "center";
  ctx.font = "900 110px 'Outfit', 'Helvetica Neue', Arial, sans-serif";
  ctx.fillStyle = "#3d2a00";
  ctx.fillText(milestone ? "INCREDIBLE!" : "HOORAY!", W / 2, 870);

  // Name
  ctx.font = "700 54px 'Outfit', 'Helvetica Neue', Arial, sans-serif";
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText(userName || "Piano Star", W / 2, 935);

  // Total counter
  ctx.font = "900 150px 'Outfit', 'Helvetica Neue', Arial, sans-serif";
  ctx.fillStyle = "#3d2a00";
  ctx.fillText(String(total), W / 2, 1070);

  ctx.font = "800 34px 'Outfit', 'Helvetica Neue', Arial, sans-serif";
  ctx.fillStyle = "#3d2a00";
  ctx.fillText("PIECES MASTERED", W / 2, 1108);

  // What was just mastered
  if (itemTitle) {
    ctx.font = "600 28px 'Outfit', 'Helvetica Neue', Arial, sans-serif";
    ctx.fillStyle = "#FFFFFF";
    const clean = itemTitle.replace(/[🎹🎵]/g, "").trim();
    const t = clean.length > 48 ? clean.slice(0, 45) + "…" : clean;
    ctx.fillText(t, W / 2, 1155);
  }

  // Footer band (dark) spanning bottom 170px — hosts QR + branding
  const footerH = 170;
  const footerY = H - footerH;
  ctx.fillStyle = "#3d2a00";
  ctx.fillRect(0, footerY, W, footerH);

  const siteUrl = (typeof window !== "undefined" && window.location && window.location.origin)
    ? window.location.origin
    : "https://pianoteacherplanner.app";
  const displayUrl = siteUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");

  // QR code rendered off-canvas, then drawn on the right side of the footer
  const qrSize = 130;
  try {
    const qrDataUrl = await QRCode.toDataURL(siteUrl, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: qrSize,
      color: { dark: "#3d2a00", light: "#FFFFFF" },
    });
    const qrImg = new Image();
    qrImg.src = qrDataUrl;
    await qrImg.decode();
    const qrX = W - qrSize - 40;
    const qrY = footerY + (footerH - qrSize) / 2;
    ctx.fillStyle = "#FFFFFF";
    if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20, 12); ctx.fill(); }
    else ctx.fillRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20);
    ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
  } catch (e) {
    console.warn("Trophy QR generation failed, continuing without QR", e);
  }

  // "Powered by" + URL — left-aligned inside footer, with branded logo
  let logoDrawnWidth = 0;
  try {
    const logo = new Image();
    logo.crossOrigin = "anonymous";
    logo.src = "/octave-icon.png";
    await logo.decode();
    const logoH = 56;
    const logoW = (logo.width / logo.height) * logoH;
    ctx.drawImage(logo, 50, footerY + 38, logoW, logoH);
    logoDrawnWidth = logoW + 14;
  } catch (e) {
    console.warn("Trophy footer logo failed to load, falling back to emoji", e);
    ctx.font = "700 36px 'Outfit', 'Helvetica Neue', Arial, sans-serif";
    ctx.fillStyle = "#FFE28A";
    ctx.textAlign = "left";
    ctx.fillText("🎹", 50, footerY + 75);
    logoDrawnWidth = 56;
  }
  ctx.textAlign = "left";
  ctx.font = "700 30px 'Outfit', 'Helvetica Neue', Arial, sans-serif";
  ctx.fillStyle = "#FFE28A";
  ctx.fillText(`Powered by ${BRAND}`, 50 + logoDrawnWidth, footerY + 70);
  ctx.font = "600 24px 'Outfit', sans-serif";
  ctx.fillStyle = "#FFFFFF";
  ctx.globalAlpha = 0.9;
  ctx.fillText(displayUrl, 50 + logoDrawnWidth, footerY + 108);
  ctx.globalAlpha = 1;
  ctx.font = "600 18px 'Outfit', sans-serif";
  ctx.fillText("Scan to join →", 50 + logoDrawnWidth, footerY + 138);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
}

export async function shareTrophyImage({ userName, total, itemTitle, milestone }) {
  const blob = await generateTrophyImage({ userName, total, itemTitle, milestone });
  const filename = `piano-${total}-mastered.png`;
  const suffix = ordinal(total);
  const shareText = `${userName || "I"} just hit ${total}${suffix} mastered piece on ${BRAND}! 🎹🏆`;

  // 1) Preferred path: native share sheet with the file attached
  try {
    const file = new File([blob], filename, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: `${total} mastered!`,
        text: shareText,
      });
      return { ok: true, method: "native-share" };
    }
  } catch (e) {
    if (e && e.name === "AbortError") return { ok: false, method: "cancelled" };
    // fall through to download
  }

  // 2) Fallback: trigger a download
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
  return { ok: true, method: "download" };
}
